import { fetch } from 'helpers/fetch.js';
import { CREATED_USER_ENTITY } from 'components/Pages/Settings/actions.js';

const defaultState = {
  data: {
    blueprint_account: {
      expiration_date: null,
    },
    subscription: null,
    email: null,
    access_role: null,
    products: [],
    name: '',
    available_accounts: [],
  },
  invite: {
    fetched: false,
    fetching: false,
    success: null,
    error: null,
    emails: null,
  },
  fetched: false,
  fetching: false,
  isAuthenticated: null,
  isRegistered: null,
  error: null,
  resetPassword: {
    emailSent: false,
    success: false,
    error: null,
  },
  newMsmt: null,
  isKnotchAdmin: null,
  isAccountAdmin: null,
  isAccountSelected: false,
  maintenance: false,
  canEditSmartUnits: false,
  currentProduct: 'blueprint',
  userEntity: null,
};

/**
 * @desc the only values needed in the account are the subscription tier
 * and the email. The account check is performed on every page load and
 * handles the svc-feed account - the admin status is given by access_role.
 *
 * @param {Object} state
 * @param {Object} action
 * @return {Object} State
 */

function accountCheck(state, action) {
  let accountData = state.data;
  let newMsmt = null;
  let isKnotchAdmin = false;
  let isAccountAdmin = false;
  let isAccountSelected = false;
  let canEditSmartUnits = false;
  let userEntity = null;
  let availableAccounts = [];
  const maintenance = action.data && action.data.maintenance_mode === true;
  const isAuthenticated = action.status === 'success';

  if (action.data && action.data.csrf_token) {
    fetch.setCsrfToken(action.data.csrf_token);
  }

  if (action.data && isAuthenticated) {
    const { email, access_role, available_accounts, blueprint_user_entity, account:
      { name, products, subscription, new_measurement_access, account_id }, can_edit_smart_units } = action.data;

    accountData = { name, subscription, email, access_role, products, available_accounts };

    if (action.data.account) {
      if (available_accounts) {
        availableAccounts = available_accounts.map(acct => ({
          ...acct,
          active: acct.account_id === account_id,
        }));
      }

      newMsmt = new_measurement_access;
      isAccountSelected = action.data.account.account_id !== null;
      accountData = {
        ...accountData,
        available_accounts: availableAccounts,
      };
    }

    isKnotchAdmin = action.data.staff === true;
    isAccountAdmin = action.data.account.admin === true;
    canEditSmartUnits = can_edit_smart_units;
    userEntity = blueprint_user_entity || null;
  }

  return Object.assign({}, state, {
    fetching: false,
    fetched: true,
    data: accountData,
    newMsmt,
    isAccountSelected,
    isKnotchAdmin,
    isAccountAdmin,
    isAuthenticated,
    canEditSmartUnits,
    userEntity,
    maintenance,
  });
}

/**
 * @desc handles the legacy account. if the login has a success status,
 * we consider the user authenticated. The admin status is given by the
 * organization_name.
 *
 * @param {Object} state
 * @param {Object} action
 * @return {Object} State
 */

function loginCheck(state, action) {
  return Object.assign({}, state, {
    isAuthenticated: action.status === 'success',
    error: action.status === 'error' && action.details,
  });
}

const AccountReducer = (state = defaultState, action) => {
  switch (action.type) {
    case '@@router/LOCATION_CHANGE':
      return { ...state, resetPassword: defaultState.resetPassword };
    case 'LOGGED_IN':
      return loginCheck(state, action);
    case 'LOGGED_OUT':
      return { ...defaultState, error: state.error };
    case 'SWITCH_ACCOUNT':
      return { ...state, isAccountSelected: false };
    case 'SWITCHED_ACCOUNT':
      return { ...state };
    case 'FETCH_ACCOUNT_INFO':
      return Object.assign({}, state, {
        fetching: true,
        fetched: false,
        data: defaultState.data,
        isAuthenticated: false,
        currentProduct: action.product,
      });
    case 'FETCHED_ACCOUNT_INFO':
      return accountCheck(state, action);
    case CREATED_USER_ENTITY:
      return Object.assign({}, state, { userEntity: action.entity });
    case 'INVITE_USER':
      return {
        ...state,
        invite: { ...action },
      };
    case 'RESET_INVITE':
      return {
        ...state,
        invite: { ...defaultState.invite },
      };
    case 'SIGN_UP':
      return { ...state, error: null };
    case 'ACCEPT_INVITE':
    case 'SIGNED_UP':
      return {
        ...state,
        logging: false,
        isRegistered: action.status === 'success',
        fetching: false,
        fetched: false,
        data: {
          subscription: 'basic',
          email: action.email,
        },
        error: action.status === 'error' && action.details,
      };
    case 'TOGGLE_MODAL':
      return {
        ...state,
        error: null,
        resetPassword: {
          ...state.resetPassword,
          emailSent: false,
          error: null,
          success: false,
        },
      };
    case 'RESETED_PASSWORD':
      return {
        ...state,
        resetPassword: {
          ...state.resetPassword,
          emailSent: action.emailSent,
          error: action.error,
          success: action.success,
        },
      };
    default:
      return state;
  }
};

export default AccountReducer;
